<template>
    <div class="group relative inline-flex">
        <UButton
            v-if="src"
            @click="removeAvatar()"
            icon="i-ph-x-bold"
            size="2xs"
            color="white"
            variant="solid"
            class="hidden group-hover:flex absolute -top-2 -right-2 z-10 border-2"
            :ui="{ rounded: 'rounded-full' }"
        />

        <label class="m-px relative inline-flex items-center justify-center rounded-md shadow-sm bg-transparent overflow-hidden text-gray-900 ring-1 ring-gray-300 cursor-pointer">
            <UAvatar
                size="3xl"
                :alt="placeholder"
                :src="src"
                :ui="{ rounded: 'rounded-none' }"
            />

            <div v-if="uploading" class="absolute inset-0 flex items-center justify-center bg-black/40">
                <UIcon name="i-ph-spinner" class="text-white size-6 animate-spin" />
            </div>

            <input
                type="file"
                accept="image/*"
                class="absolute inset-0 invisible"
                @change="uploadAvatar"
                :disabled="uploading"
            />
        </label>
    </div>
</template>

<script setup lang="ts">
const emit = defineEmits<{
    'update:modelValue': [string]
}>()

const props = withDefaults(defineProps<{
    modelValue?: string
    placeholder?: string
    bucket?: string
    folder?: string
}>(), {
    bucket: 'avatars',
    folder: '',
})

const supabase = useSupabaseClient()
const toast = useToast()

const uploading = ref(false)
const src = ref('')
const localFileSet = ref(false)

watch(() => props.modelValue, (val) => {
    if (localFileSet.value) return
    if (val) {
        downloadImage()
    } else {
        src.value = ''
    }
}, { immediate: true })

async function downloadImage() {
    if (!props.modelValue) return
    try {
        const objectPath = props.folder ? `${props.folder}/${props.modelValue}` : props.modelValue
        const { data, error } = await supabase.storage.from(props.bucket).download(objectPath)
        if (error) throw error
        src.value = URL.createObjectURL(data)
    } catch (error: any) {
        console.error('Error downloading image:', error.message)
        toast.add({ title: 'Could not load image', description: error.message, color: 'red' })
    }
}

async function uploadAvatar(event: any) {
    const files = event.target?.files
    if (!files || files.length === 0) return

    const file = files[0]
    const previousSrc = src.value
    const previousLocalFlag = localFileSet.value

    src.value = URL.createObjectURL(file)
    localFileSet.value = true
    uploading.value = true

    try {
        const fileExt = file.name.split('.').pop()
        const fileName = `${Math.random()}.${fileExt}`
        const objectPath = props.folder ? `${props.folder}/${fileName}` : fileName

        const { error: uploadError } = await supabase.storage
            .from(props.bucket)
            .upload(objectPath, file)

        if (uploadError) throw uploadError

        emit('update:modelValue', fileName)
    } catch (error: any) {
        console.error('Upload failed:', error.message)
        toast.add({ title: 'Upload failed', description: error.message, color: 'red' })
        src.value = previousSrc
        localFileSet.value = previousLocalFlag
    } finally {
        uploading.value = false
    }
}

function removeAvatar() {
    src.value = ''
    localFileSet.value = false
    emit('update:modelValue', '')
}
</script>
